import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Cloud transition shared between the adventure map (clouds close IN as you
 * zoom into a game) and the destination game page (clouds part OPEN to reveal
 * it). The map covers the screen, navigates, and this overlay — mounted once
 * at app level — starts already covered on the new route and animates apart,
 * so the route swap itself is hidden inside the clouds.
 */

const REVEAL_KEY = "pm-cloud-reveal";
const REVEAL_MS = 850;

/** Called by the map just before navigating into a game. */
export function requestCloudReveal() {
  try {
    sessionStorage.setItem(REVEAL_KEY, String(Date.now()));
  } catch {
    // Storage unavailable — the reveal simply won't play.
  }
}

function consumeRevealRequest() {
  try {
    const raw = sessionStorage.getItem(REVEAL_KEY);
    if (!raw) return false;
    sessionStorage.removeItem(REVEAL_KEY);
    // Stale flags (e.g. an aborted navigation) shouldn't replay later.
    return Date.now() - Number(raw) < 3000;
  } catch {
    return false;
  }
}

/**
 * A clearly-cloud-shaped puffy cloud: overlapping circles share one white
 * fill, so they render as a single fluffy silhouette.
 */
export const Cloud = ({ className }) => (
  <svg className={className} viewBox="0 0 220 120" aria-hidden focusable="false">
    <g fill="#FFFFFF">
      <circle cx="46" cy="78" r="32" />
      <circle cx="86" cy="50" r="42" />
      <circle cx="132" cy="54" r="38" />
      <circle cx="172" cy="78" r="30" />
      <rect x="30" y="72" width="160" height="36" rx="18" />
    </g>
  </svg>
);

const CloudReveal = () => {
  const location = useLocation();
  const [phase, setPhase] = useState("idle"); // idle | cover | open
  const timersRef = useRef([]);

  useEffect(() => {
    if (!consumeRevealRequest()) return undefined;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduced) return undefined;

    setPhase("cover");
    timersRef.current.push(
      // Two frames covered so the new page paints beneath the clouds first.
      window.setTimeout(() => setPhase("open"), 90),
      window.setTimeout(() => setPhase("idle"), 90 + REVEAL_MS),
    );
    return () => {
      timersRef.current.forEach((t) => window.clearTimeout(t));
      timersRef.current = [];
    };
    // Re-check on every route change; only a fresh map launch sets the flag.
  }, [location.pathname]);

  if (phase === "idle") return null;

  return (
    <div className={`cr-stage cr-stage--${phase}`} aria-hidden>
      <div className="cr-wash" />
      <Cloud className="cr-cloud cr-cloud--l1" />
      <Cloud className="cr-cloud cr-cloud--l2" />
      <Cloud className="cr-cloud cr-cloud--r1" />
      <Cloud className="cr-cloud cr-cloud--r2" />
      <Cloud className="cr-cloud cr-cloud--b1" />
      <style>{`
        .cr-stage {
          position: fixed;
          inset: 0;
          z-index: 99998;
          pointer-events: none;
          overflow: hidden;
        }
        .cr-wash {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, #FFFFFF 0%, #F2F8FF 100%);
        }
        .cr-cloud {
          position: absolute;
          width: 110vmax;
          height: auto;
          filter: drop-shadow(0 18px 30px rgba(120,150,190,0.25)) blur(0.5px);
        }
        .cr-cloud--l1 { left: -28vmax; top: -16vmax; }
        .cr-cloud--l2 { left: -34vmax; bottom: -20vmax; width: 120vmax; }
        .cr-cloud--r1 { right: -28vmax; top: -12vmax; }
        .cr-cloud--r2 { right: -34vmax; bottom: -22vmax; width: 120vmax; }
        .cr-cloud--b1 { left: 50%; bottom: -30vmax; width: 130vmax; transform: translateX(-50%); }

        .cr-stage--open .cr-wash {
          opacity: 0;
          transition: opacity ${Math.round(REVEAL_MS * 0.45)}ms ease-out;
        }
        .cr-stage--open .cr-cloud { transition: transform ${REVEAL_MS}ms cubic-bezier(0.45, 0, 0.2, 1); }
        .cr-stage--open .cr-cloud--l1 { transform: translate(-95vmax, -18vmax); }
        .cr-stage--open .cr-cloud--l2 { transform: translate(-100vmax, 14vmax); }
        .cr-stage--open .cr-cloud--r1 { transform: translate(95vmax, -16vmax); }
        .cr-stage--open .cr-cloud--r2 { transform: translate(100vmax, 16vmax); }
        .cr-stage--open .cr-cloud--b1 { transform: translate(-50%, 95vmax); }
      `}</style>
    </div>
  );
};

export default CloudReveal;
